import AenderungsmodusPatch from '@src/model/aenderungsmodus/AenderungsmodusPatch';
import AenderungsmodusPatchBody from '@src/model/aenderungsmodus/AenderungsmodusPatchBody';
import AenderungsmodusMode from '@src/model/enums/AenderungsmodusMode';
import AenderungsmodusSectionState from '@src/model/enums/AenderungsmodusSectionState';
import { authWrapper } from '@src/services/axios';
import { deepClone, isEmpty, isEqual, toSnake } from '@st/utils-js';
import moment from 'moment';
import ClientElementLayout from '@src/model/ClientElementLayout';
import DTOClientElementLayout from '@src/model/DTOClientElementLayout';
import MandantDokumentElement from '../../../../model/MandantDokumentElement';
import MandantDokumentElementClientElement from '../../../../model/MandantDokumentElementClientElement';
import services from '../../../../services';
import Patcher from '../../../../utils/Patcher';
import types from '../../../types';
import * as helper from './helper';
import ClientElementFormularLayout from '@src/model/ClientElementFormularLayout';
import ClientElementSectionLayout from '@src/model/ClientElementSectionLayout';

const mandantEditorActions = types.actions.mandantEditor;
const mandantEditorMutations = types.mutations.mandantEditor;
const mandantEditorGetters = types.getters.mandantEditor;

const viewContentMutations = types.mutations.viewContent;

const sharedEditorMutations = types.mutations.sharedEditor;

function mapSectionSettingsBody(sections) {
  if (!sections || typeof sections !== 'object') return {};

  return Object.keys(sections).reduce((acc, sectionId) => {
    const value = sections[sectionId] ?? {};
    acc[sectionId] = {
      'page-break': Boolean(
        value['page-break'] ?? value.pageBreak ?? value.seitenumbruch
      ),
    };
    return acc;
  }, {});
}

function mapSectionsToSectionSettings(sections) {
  if (!sections || typeof sections !== 'object') return {};

  return Object.keys(sections).reduce((acc, sectionId) => {
    const section = sections[sectionId] ?? {};
    acc[sectionId] = {
      pageBreak: Boolean(section['page-break'])
    };
    return acc;
  }, {});
}

function mapFormLayoutsBody(formulare) {
  return Object.keys(formulare ?? {}).reduce((acc, key) => {
    const layout = formulare[key];

    if (layout instanceof ClientElementFormularLayout) {
      acc[key] = layout.toJSON();
    } else {
      acc[key] = layout;
    }

    return acc;
  }, {});
}

function mapFormLayoutsToStore(formulare) {
  return Object.keys(formulare ?? {}).reduce((acc, key) => {
    acc[key] = ClientElementFormularLayout.fromJSON(formulare[key]);
    return acc;
  }, {});
}

function createDocumentSettingsBody(forms, sections) {
  return {
    forms: mapFormLayoutsBody(forms),
    sections: mapSectionSettingsBody(sections)
  };
}

export default {
  [mandantEditorActions.FETCH_CLIENT_ELEMENTS]({ commit }, { clientId, queryParams }) {
    const prm = new Promise((resolve, reject) => {
      services.editor.client
        .getClientElementList(clientId, queryParams ?? '')
        .then((result) => {
          commit(mandantEditorMutations.SET_CLIENT_ELEMENTS, {
            clientElements: result?.data?.results,
            pagination: result?.data?.pagination,
          });
          resolve(result);
        })
        .catch((error) => reject(error));
    });

    return authWrapper({ commit }, prm);
  },

  [mandantEditorActions.FETCH_CLIENT_ELEMENT]({ commit }, { elementId, revision, subElementId }) {
    const prm = new Promise((resolve, reject) => {
      services.editor.client
        .getClientElement(elementId, revision)
        .then((response) => {
          const tmpClientElement = MandantDokumentElement.fromJSON(response.data);

          if (!!subElementId && !tmpClientElement.clientElement.subElements[subElementId]) {
            const error = new Error('Dokument im Vertrag nicht gefunden');
            error.response = {
              status: 404,
            };
            reject(error);
          }

          commit(mandantEditorMutations.SET_CLIENT_ELEMENT, tmpClientElement);
          resolve(tmpClientElement);
        })
        .catch((error) => {
          commit(
            `${viewContentMutations.SHOW_SNACKBAR_ERROR}`,
            'Vertrag/Dokument ist nicht valide'
          );
          reject(error);
        });
    });

    return authWrapper({ commit }, prm);
  },
  [mandantEditorActions.FETCH_LAYOUT_BY_LAYOUT_ID]({ commit, dispatch }, { clientId, templateId }) {
    const prm = new Promise((resolve, reject) => {
      services.editor.layout
        .layoutById(clientId, templateId)
        .then((result) => {
          const { template = {}, name, revision } = result.data;

          commit(mandantEditorMutations.SET_LIVE_VIEW_DOC_LAYOUT_NAME, name);
          commit(mandantEditorMutations.SET_LIVE_VIEW_DOC_LAYOUT_REVISION, Number(revision));

          const layoutIsEmpty = Object.keys(template).length === 0;

          const tmpClientElementLayout = ClientElementLayout.fromJSON(template);

          if (layoutIsEmpty) {
            const dotLayout = DTOClientElementLayout.fromJson(tmpClientElementLayout);

            dispatch(mandantEditorActions.SET_LAYOUT_KEY_FOR_LAYOUT, {
              clientId,
              layout: dotLayout.toJSON(),
              templateId,
              revision,
            });
          }

          commit(mandantEditorMutations.SET_CLIENT_ELEMENT_LAYOUT, {
            layout: tmpClientElementLayout,
            sectionLayouts: (result.data.sectionLayouts ?? []).map((sectionLayout) =>
              ClientElementSectionLayout.fromJSON(sectionLayout)
            )
          });
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });

    return authWrapper({ commit }, prm);
  },
  [mandantEditorActions.FETCH_ONLY_LAYOUT_BY_LAYOUT_ID]
  ({ commit }, { clientId, templateId }) {
    const prm = new Promise((resolve, reject) => {
      services.editor.layout
        .layoutById(clientId, templateId)
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });

    return authWrapper({ commit }, prm);
  },

  [mandantEditorActions.SET_LAYOUT_FOR_CLIENT_ELEMENT](
    { commit },
    { clientId, clientElementId, subElementId, templateId }
  ) {
    const body = {
      templateId,
    };

    const prm = new Promise((resolve, reject) => {
      services.editor.layout
        .setLayoutForClientElement(clientId, clientElementId, subElementId, body)
        .then((result) => {
          const mappedLayout = ClientElementLayout.fromJSON(result.data.template);

          commit(mandantEditorMutations.SET_CLIENT_ELEMENT_LAYOUT, {
            layout: mappedLayout,
          });

          commit(mandantEditorMutations.SET_LIVE_VIEW_DOC_LAYOUT_NAME, result.data.templateName);

          commit(mandantEditorMutations.SET_CLIENT_ELEMENT_TEMPLATES, {
            subElementId,
            templateId,
          });
          commit(mandantEditorMutations.SET_LIVE_VIEW_DOC_LAYOUT_REVISION, result.data.revision);

          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });

    return authWrapper({ commit }, prm);
  },

  [mandantEditorActions.INSERT_NEW_TEMPLATE]({ commit }, { clientId, subElementId }) {
    const prm = new Promise((resolve, reject) => {
      const body = {
        name: 'Neues Template',
        layout: new ClientElementLayout().toDTO().toJSON(),
      };

      services.editor.layout
        .insertLayout(clientId, body)
        .then((result) => {
          const mappedLayout = ClientElementLayout.fromJSON(result.data.template);

          commit(mandantEditorMutations.SET_CLIENT_ELEMENT_LAYOUT, {
            layout: mappedLayout,
          });
          commit(mandantEditorMutations.SET_CLIENT_ELEMENT_TEMPLATES, {
            subElementId,
            templateId: result.data.templateId,
          });
          commit(mandantEditorMutations.SET_LIVE_VIEW_DOC_LAYOUT_NAME, 'Neues Template');

          commit(mandantEditorMutations.SET_LIVE_VIEW_DOC_LAYOUT_REVISION, 0);

          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });

    return authWrapper({ commit }, prm);
  },

  [mandantEditorActions.DUPLICATE_LAYOUT](
    { commit },
    { clientId, clientElementId, subElementId, templateId }
  ) {
    const prm = new Promise((resolve, reject) => {
      services.editor.layout
        .duplicateLayout(clientId, templateId, clientElementId, subElementId)
        .then((result) => {
          const mappedLayout = ClientElementLayout.fromJSON(result.data.template);

          commit(mandantEditorMutations.SET_CLIENT_ELEMENT_LAYOUT, {
            layout: mappedLayout,
          });
          commit(mandantEditorMutations.SET_CLIENT_ELEMENT_TEMPLATES, {
            subElementId,
            templateId: result.data.templateId,
          });
          commit(mandantEditorMutations.SET_LIVE_VIEW_DOC_LAYOUT_REVISION, 0);
          commit(mandantEditorMutations.SET_LIVE_VIEW_DOC_LAYOUT_NAME, result.data.templateName);

          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });

    return authWrapper({ commit }, prm);
  },

  [mandantEditorActions.SET_LAYOUT_KEY_FOR_LAYOUT](
    { commit },
    { clientId, layout, templateId, revision }
  ) {
    const prm = new Promise((resolve, reject) => {
      // clientId, templateId, revision, body
      services.editor.layout
        .setLayoutKeyForLayout(clientId, templateId, revision, layout)
        .then((result) => {
          Object.keys(layout).forEach((key) => {
            commit(mandantEditorMutations.SET_CLIENT_ELEMENT_LAYOUT_KEY_VALUE, {
              key,
              value: layout[key]
            });
          });

          const newRevision = Number(revision) + 1;
          commit(mandantEditorMutations.SET_LIVE_VIEW_DOC_LAYOUT_REVISION, newRevision);

          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });

    return authWrapper({ commit }, prm);
  },

  [mandantEditorActions.SET_CLIENT_ELEMENT_LAYOUT_KEY_VALUE](
    { commit },
    { clientElementId, key, value }
  ) {
    const prm = new Promise((resolve, reject) => {
      const mappedKey = toSnake(key);

      services.editor.client
        .putClientElementLayoutKeyValue(clientElementId, mappedKey, value)
        .then((result) => {
          commit(mandantEditorMutations.SET_CLIENT_ELEMENT_LAYOUT_KEY_VALUE, {
            key,
            value
          });
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });

    return authWrapper({ commit }, prm);
  },

  [mandantEditorActions.SET_CLIENT_ELEMENT_SECTION_LAYOUT](
    { commit },
    { clientElementId, sectionId, layout }
  ) {
    // remapping normalizes legacy section colors before persistence
    const normalizedLayout = ClientElementSectionLayout.fromJSON(layout);
    const prm = new Promise((resolve, reject) => {
      services.editor.client
        .putSectionLayout(clientElementId, sectionId, normalizedLayout)
        .then((result) => {
          commit(mandantEditorMutations.SET_CLIENT_ELEMENT_SECTION_LAYOUT, {
            sectionId,
            layout: normalizedLayout
          });
          commit(sharedEditorMutations.UPDATE_SECTION_RERENDER_MAP, {
            id: sectionId,
          });
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });

    return authWrapper({ commit }, prm);
  },
  [mandantEditorActions.DELETE_CLIENT_ELEMENT_SECTION_LAYOUT](
    { commit },
    { clientElementId, sectionId }
  ) {
    const prm = new Promise((resolve, reject) => {
      services.editor.client
        .deleteSectionLayout(clientElementId, sectionId)
        .then((result) => {
          commit(mandantEditorMutations.DELETE_CLIENT_ELEMENT_SECTION_LAYOUT, {
            sectionId,
          });
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });

    return authWrapper({ commit }, prm);
  },

  [mandantEditorActions.FETCH_FORMULAR_LAYOUTS](
    { commit, state = {} },
    { clientId, clientElementId, subelementId }
  ) {
    const hasTemplateForSubelement = state.clientElement?.templates?.[subelementId] !== undefined;

    const prm = new Promise((resolve, reject) => {
      if (state.clientElement?.templates && !hasTemplateForSubelement) {
        commit(mandantEditorMutations.SET_LIVE_VIEW_DOC_FORMULAR_LAYOUTS, {});
        commit(mandantEditorMutations.SET_LIVE_VIEW_DOC_FORMULAR_LAYOUT_REVISION, 0);
        resolve({
          data: {
            formulare: {},
            revision: 0
          }
        });
        return;
      }

      services.editor.layout
        .documentSettings(clientId, clientElementId, subelementId)
        .then((result) => {
          // keep the read side backward compatible while the backend payload is still changing
          const forms = result.data.forms ?? result.data.formulare ?? {};
          const sections = result.data.sections ?? {};
          const preparedFormulare = mapFormLayoutsToStore(forms);
          const preparedSectionSettings = mapSectionsToSectionSettings(
            mapSectionSettingsBody(sections)
          );

          commit(mandantEditorMutations.SET_LIVE_VIEW_DOC_FORMULAR_LAYOUTS, preparedFormulare);
          commit(
            mandantEditorMutations.SET_LIVE_VIEW_SECTION_SETTINGS,
            preparedSectionSettings
          );
          commit(
            mandantEditorMutations.SET_LIVE_VIEW_DOCUMENT_SETTINGS_REVISION,
            result.data.revision
          );
          commit(mandantEditorMutations.SET_LIVE_VIEW_DOCUMENT_SETTINGS_LOADED, true);

          resolve(result);
        })
        .catch((error) => {
          commit(mandantEditorMutations.SET_LIVE_VIEW_DOCUMENT_SETTINGS_LOADED, true);
          console.log('document settings do not exist: ' + error);
          if (error?.response?.status === 404 || !error?.response) {
            commit(mandantEditorMutations.SET_LIVE_VIEW_DOC_FORMULAR_LAYOUTS, {});
            commit(mandantEditorMutations.SET_LIVE_VIEW_DOC_FORMULAR_LAYOUT_REVISION, 0);
            resolve({
              data: {
                formulare: {},
                revision: 0
              }
            });
            return;
          }

          reject(error);
        });
    });

    return authWrapper({ commit }, prm);
  },

  [mandantEditorActions.SET_FORMULAR_LAYOUTS](
    { commit, state },
    { clientId, clientElementId, subelementId, documentSettingsRevision, formulare, sections }
  ) {
    const nextForms = formulare ?? state.liveViewDocFormularLayouts ?? {};
    const nextSections = sections ?? state.liveViewSectionSettings ?? {};
    const body = createDocumentSettingsBody(nextForms, nextSections);

    const prm = new Promise((resolve, reject) => {
      services.editor.layout
        .setDocumentSettings(
          clientId,
          clientElementId,
          subelementId,
          documentSettingsRevision,
          body
        )
        .then((result) => {
          const mappedLayout = Object.keys(nextForms).reduce((acc, key) => {
            acc[key] =
              nextForms[key] instanceof ClientElementFormularLayout
                ? nextForms[key]
                : ClientElementFormularLayout.fromJSON(body.forms[key]);
            return acc;
          }, {});

          commit(mandantEditorMutations.SET_LIVE_VIEW_DOC_FORMULAR_LAYOUTS, mappedLayout);
          commit(
            mandantEditorMutations.SET_LIVE_VIEW_SECTION_SETTINGS,
            mapSectionsToSectionSettings(body.sections)
          );

          commit(
            mandantEditorMutations.SET_LIVE_VIEW_DOCUMENT_SETTINGS_REVISION,
            documentSettingsRevision + 1
          );

          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });

    return authWrapper({ commit }, prm);
  },

  [mandantEditorActions.PUT_CLIENT_ELEMENT_METADATA](
    { commit },
    { clientElementId, clientElementName, clientElementNotes }
  ) {
    const prm = new Promise((resolve, reject) => {
      services.editor.client
        .putClientElementMetadata({
          clientElementId,
          clientElementName,
          clientElementNotes,
        })
        .then(() => {
          commit(mandantEditorMutations.SET_CLIENT_ELEMENT_NAME, {
            clientElementId,
            clientElementName,
          });
          commit(mandantEditorMutations.SET_CLIENT_ELEMENT_NOTES, {
            clientElementId,
            clientElementNotes,
          });
          resolve(clientElementName);
        })
        .catch((error) => reject(error));
    });

    return authWrapper({ commit }, prm);
  },

  /**
   * Erstellt ein neues Dokument und es wird ein Patch an die Middleware verschickt.
   * @param {*} param0
   * @param {MandantDokumentElementClientElement} clientElement
   */
  [mandantEditorActions.ADD_CLIENT_ELEMENT_NEW_ELEMENT](
    { commit },
    { clientElementId, revision, clientElement, newSubelement }
  ) {
    if (!(clientElement instanceof MandantDokumentElementClientElement)) {
      return Promise.resolve(null);
    }

    const currentClientElementJSON = deepClone(clientElement.toJSON());
    const currentClientElement =
      MandantDokumentElementClientElement.fromJSON(currentClientElementJSON);

    currentClientElement.addSubelement(newSubelement);

    const patches = Patcher.createPatches(clientElement.toJSON(), currentClientElement.toJSON());

    const prm = new Promise((resolve, reject) => {
      services.editor.client
        .patchClientElement({
          clientElementId,
          patches,
          revision,
        })
        .then(() => {
          resolve(newSubelement);
        })
        .catch((error) => {
          const msg = 'Dokument konnte nicht hinzugefügt werden';
          commit(viewContentMutations.SHOW_SNACKBAR_ERROR, msg);
          reject(error);
        });
    });

    return authWrapper({ commit }, prm);
  },

  /**
   * Löscht ein Dokument und es wird ein Patch an die Middleware verschickt.
   * @param {*} param0
   * @param {MandantDokumentElementClientElement} clientElement
   */
  [mandantEditorActions.REMOVE_CLIENT_ELEMENT_SUBELEMENT](
    { commit },
    { clientElementId, revision, clientElement, subElementId }
  ) {
    if (!(clientElement instanceof MandantDokumentElementClientElement)) {
      return Promise.resolve(false);
    }
    const currentClientElementJSON = deepClone(clientElement.toJSON());
    const currentClientElement =
      MandantDokumentElementClientElement.fromJSON(currentClientElementJSON);

    currentClientElement.removeSubelement(subElementId);

    const patches = Patcher.createPatches(clientElement.toJSON(), currentClientElement.toJSON());

    const prm = new Promise((resolve, reject) => {
      services.editor.client
        .patchClientElement({
          clientElementId,
          patches,
          revision,
        })
        .then(() => {
          resolve(true);
        })
        .catch((error) => {
          const msg = 'Element konnte nicht gelöscht werden';
          commit(viewContentMutations.SHOW_SNACKBAR_ERROR, msg);
          reject(error);
        });
    });

    return authWrapper({ commit }, prm);
  },

  [mandantEditorActions.DEPRECATE_CLIENT_ELEMENT]({ commit }, { clientElementId }) {
    const prm = new Promise((resolve, reject) => {
      services.editor.client
        .deleteClientElement(clientElementId)
        .then((result) => {
          commit(mandantEditorMutations.DEPRECATE_CLIENT_ELEMENT, { clientElementId });
          resolve(result);
        })
        .catch((error) => {
          commit(viewContentMutations.SHOW_SNACKBAR_ERROR, error.message);

          reject(error);
        });
    });

    return authWrapper({ commit }, prm);
  },

  [mandantEditorActions.PUT_CLIENT_ELEMENT_RELEASE_FLAG](
    { commit },
    { clientElementId, freigabeStatus }
  ) {
    const prm = new Promise((resolve, reject) => {
      services.editor.client
        .putClientElementReleaseFlag(clientElementId, freigabeStatus)
        .then((result) => {
          commit(mandantEditorMutations.SET_CLIENT_ELEMENT_RELEASE_FLAG, {
            clientElementId,
            freigabeStatus,
          });
          resolve(result);
        })
        .catch((error) => {
          commit(viewContentMutations.SHOW_SNACKBAR_ERROR, error.message);

          reject(error);
        });
    });

    return authWrapper({ commit }, prm);
  },

  [mandantEditorActions.PUT_CLIENT_ELEMENT_TAGS]({ commit }, { clientElementId, tagsObj }) {
    const tagIds = tagsObj.tags.map((tag) => tag.id);
    const prm = new Promise((resolve, reject) => {
      services.editor.client
        .putClientElementTags(clientElementId, { tags: tagIds })
        .then((result) => {
          commit(mandantEditorMutations.SET_CLIENT_ELEMENT_TAGS, {
            clientElementId,
            tags: tagsObj.tags,
          });
          resolve(result);
        })
        .catch((error) => {
          commit(viewContentMutations.SHOW_SNACKBAR_ERROR, error.message);

          reject(error);
        });
    });

    return authWrapper({ commit }, prm);
  },

  [mandantEditorActions.GET_CLIENT_ELEMENT_UPDATES]({ commit }, { clientElementId }) {
    const prm = new Promise((resolve, reject) => {
      services.editor.client
        .getClientElementUpdates(clientElementId)
        .then((result) => {
          commit(mandantEditorMutations.SET_CLIENT_ELEMENT_UPDATES, {
            updates: result.data.updates,
          });
          resolve(result);
        })
        .catch((error) => {
          commit(viewContentMutations.SHOW_SNACKBAR_ERROR, error.message);

          reject(error);
        });
    });

    return authWrapper({ commit }, prm);
  },

  // Updates
  [mandantEditorActions.GET_CLIENT_ELEMENT_ZWANGSUPDATE]({ commit }, { clientElementId }) {
    return new Promise((resolve, reject) => {
      services.client.update
        .getZwangsupdate(clientElementId)
        .then((result) => {
          const zwangsupdate = result.data;
          commit(mandantEditorMutations.SET_CLIENT_ELEMENT_ZWANGSUPDATE, zwangsupdate);
          resolve(result);
        })
        .catch((error) => reject(new Error(error.message)));
    });
  },
  [mandantEditorActions.SET_UPDATE_COMPLETED]({ commit }, { clientId, clientElementId }) {
    const prm = new Promise((resolve, reject) => {
      services.editor.mandantUpdate
        .setUpdateCompleted(clientId, clientElementId)
        .then((result) => {
          commit(mandantEditorMutations.SET_UPDATE_COMPLETED, { clientElementId });
          commit(mandantEditorMutations.UPDATE_MANDANT_REVISION);
          resolve(result);
        })
        .catch((error) => {
          commit(viewContentMutations.SHOW_SNACKBAR_ERROR, error.message);

          reject(error);
        });
    });

    return authWrapper({ commit }, prm);
  },
  [mandantEditorActions.REMOVE_CLIENT_ELEMENT_ZWANGSUPDATE_KEY](
    { state, commit },
    { clientId, clientElementId, sectionId }
  ) {
    if (!state.clientElementZwangsupdate?.[sectionId]) return null;

    const prm = new Promise((resolve, reject) => {
      services.editor.mandantUpdate
        .deleteZwangsupdateSection(clientId, clientElementId, sectionId)
        .then((result) => {
          commit(mandantEditorMutations.REMOVE_CLIENT_ELEMENT_ZWANGSUPDATE_KEY, sectionId);
          resolve(result);
        })
        .catch((error) => {
          commit(viewContentMutations.SHOW_SNACKBAR_ERROR, error.message);

          reject(error);
        });
    });

    return authWrapper({ commit }, prm);
  },

  [mandantEditorActions.START_AENDERUNGSMODUS](
    { commit, dispatch },
    { clientElementId, subElementId }
  ) {
    const prm = new Promise((resolve, reject) => {
      services.mandant.aenderungsmodus
        .startRecording(clientElementId)
        .then((result) => {
          dispatch(mandantEditorActions.GET_AENDERUNGSMODUS_HISTORY, {
            clientElementId,
            subElementId,
          });
          resolve(result);
        })
        .catch((error) => {
          commit(viewContentMutations.SHOW_SNACKBAR_ERROR, error.message);

          reject(error);
        });
    });

    return authWrapper({ commit }, prm);
  },

  [mandantEditorActions.END_AENDERUNGSMODUS]({ commit }, { clientElementId, mode }) {
    if (!AenderungsmodusMode.isValidState(mode)) throw new Error('Invalid mode');

    const prm = new Promise((resolve, reject) => {
      services.mandant.aenderungsmodus
        .stopRecording(clientElementId, mode)
        .then((result) => {
          commit(mandantEditorMutations.SET_AENDERUNGSMODUS, false);
          resolve(result);
        })
        .catch((error) => {
          commit(viewContentMutations.SHOW_SNACKBAR_ERROR, error.message);

          reject(error);
        });
    });

    return authWrapper({ commit }, prm);
  },

  [mandantEditorActions.GET_AENDERUNGSMODUS_HISTORY](
    { state, commit },
    { clientElementId, subElementId }
  ) {
    const prm = new Promise((resolve, reject) => {
      services.mandant.aenderungsmodus
        .getHistory(clientElementId, subElementId)
        .then((result) => {
          commit(mandantEditorMutations.SET_AENDERUNGSMODUS_HISTORY, result.data);
          commit(mandantEditorMutations.SET_AENDERUNGSMODUS, result.status);
          if (result.status) {
            commit(mandantEditorMutations.PREPARE_CLIENT_ELEMENT_FOR_AENDERUNGSMODUS, {
              originClientElement: state.clientElement,
              subelementRecord: result.data.subelementRecord,
              subElementId,
              aenderungsmodusSections: result.data.sections,
            });
          }
          resolve(result.status);
        })
        .catch((error) => {
          commit(viewContentMutations.SHOW_SNACKBAR_ERROR, error.message);

          reject(error);
        });
    });

    return authWrapper({ commit }, prm);
  },

  [mandantEditorActions.SET_AENDERUNGSMODUS_SECTION_STATE](
    { state, commit, rootState },
    { sectionId }
  ) {
    const section = state.aenderungsmodusHistory.sections[sectionId];
    const latestSectionState = section?.patches.at(-1)?.state;
    const sectionState = AenderungsmodusSectionState.isNew(latestSectionState)
      ? AenderungsmodusSectionState.NEW_DELETED
      : AenderungsmodusSectionState.EXISTING_DELETED;

    const { order } =
      state.clientElement.clientElement.subElements[state.currentClientElementId].content;

    const patchBody = new AenderungsmodusPatchBody([], sectionState, order).toJSON();

    return services.mandant.aenderungsmodus
      .patchChanges({
        clientElementId: state.clientElement.clientElementId,
        subElementId: state.currentClientElementId,
        sectionId,
        baseRevision: section?.baseRevision ?? 0,
        baseSubRevision: section?.baseSubRevision ?? 0,
        body: patchBody,
      })
      .then((response) => {
        const patch = new AenderungsmodusPatch(
          rootState.user.user.id,
          rootState.user.user.firstname,
          rootState.user.user.lastname,
          Number(moment().format('x')),
          [],
          sectionState,
          response.data.nextBaseRevision,
          response.data.nextBaseSubRevision,
          [],
          response.data.isUpdatePatch
        );

        commit(mandantEditorMutations.SET_AENDERUNGSMODUS_SECTION_REVISION_AND_ADD_PATCH, {
          sectionId,
          revision: response.data.nextBaseRevision,
          subRevision: response.data.nextBaseSubRevision,
          patch,
        });

        commit(sharedEditorMutations.UPDATE_SECTION_RERENDER_MAP, {
          id: sectionId,
        });

        commit(mandantEditorMutations.SET_AENDERUNGSMODUS_SELECTED_HEAD, patch);
      });
  },

  [mandantEditorActions.PATCH_AENDERUNGSMODUS_PATCHES](
    { state, commit, getters, rootState },
    {
      clientElementId,
      subElementId,
      newSection,
      oldSection,
      sectionId,
      order,
      elementController,
      elementControllerOptions = {},
    }
  ) {
    const patches = Patcher.createPatches(oldSection, newSection);

    if (patches.length < 1) return Promise.resolve();

    const historySection =
      getters[mandantEditorGetters.GET_AENDERUNGSMODUS_HISTORY_SECTION](sectionId);
    const oldOrder = state.clientElement.clientElement.subElements[subElementId].content.order;
    let historySectionState = helper.getAenderungsmodusSectionState(
      sectionId,
      order,
      oldOrder,
      state.aenderungsmodusHistory
    );
    if (isEmpty(oldSection)) {
      historySectionState = AenderungsmodusSectionState.NEW;
    }

    const orderChanged = !isEqual(order, oldOrder);

    const patchBody = new AenderungsmodusPatchBody(patches, historySectionState, order).toJSON();

    const { user } = rootState.user;

    return elementController.savePatches(
      {
        clientElementId,
        subElementId,
        sectionId,
        baseRevision: 0,
        baseSubRevision: 0,
        body: patchBody,
      },
      services.mandant.aenderungsmodus.patchChanges,
      {
        preparePayloadFn: (payload) => {
          const aenderungsmodusHistorySection =
            getters[mandantEditorGetters.GET_AENDERUNGSMODUS_HISTORY_SECTION](sectionId);
          // eslint-disable-next-line no-param-reassign
          payload.baseRevision = aenderungsmodusHistorySection?.baseRevision ?? 0;
          // eslint-disable-next-line no-param-reassign
          payload.baseSubRevision = aenderungsmodusHistorySection?.baseSubRevision ?? 0;

          const newPatch = helper.aenderungsmodusHeadIsSetAndRevisionOfSectionIsNotLatest(
            state.aenderungsmodusSelectedHead,
            historySection
          );

          // eslint-disable-next-line no-param-reassign
          payload.newPatch = newPatch;
        },
        everySucessfullRun: (_, responseBody) => {
          const patch = new AenderungsmodusPatch(
            user.id,
            user.firstname,
            user.lastname,
            Number(moment().format('x')),
            responseBody.payload.body.patch,
            responseBody.payload.body.state,
            responseBody.response.data.nextBaseRevision,
            responseBody.response.data.nextBaseSubRevision,
            [],
            responseBody.response.data.isUpdatePatch
          );

          commit(mandantEditorMutations.SET_AENDERUNGSMODUS_SECTION_REVISION_AND_ADD_PATCH, {
            sectionId: responseBody.payload.sectionId,
            revision: responseBody.response.data.nextBaseRevision,
            subRevision: responseBody.response.data.nextBaseSubRevision,
            patch,
          });

          const newHead =
            responseBody.payload.baseRevision < responseBody.response.data.nextBaseRevision;
          if (newHead) {
            commit(mandantEditorMutations.SET_AENDERUNGSMODUS_SELECTED_HEAD, patch);
          } else {
            const currentHead = state.aenderungsmodusSelectedHead;
            const updatedHead = AenderungsmodusPatch.fromJSON({
              ...currentHead,
              revision: responseBody.response.data.nextBaseRevision,
              subRevision: responseBody.response.data.nextBaseSubRevision,
            });
            commit(mandantEditorMutations.SET_AENDERUNGSMODUS_SELECTED_HEAD, updatedHead);
          }

          if (orderChanged) {
            commit(mandantEditorMutations.SET_AENDERUNGSMODUS_ORIGIN_CLIENT_ELEMENT_ORDER, order);
          }
        },
        // chunk by section id so patches from different sections are never grouped together
        payloadChunkKey: 'sectionId',
        chunkPayloadKey: 'body.patch',
        ...elementControllerOptions,
      }
    );
  },

  [mandantEditorActions.ADD_COMMENT_TO_PATCH](
    { state, commit, rootState },
    { subElementId, sectionId, revision, comment }
  ) {
    const { clientElementId } = state.clientElement;

    return services.mandant.aenderungsmodus
      .addCommentToPatch(clientElementId, subElementId, sectionId, revision, comment)
      .then(() => {
        commit(mandantEditorMutations.ADD_COMMENT_TO_PATCH, {
          sectionId,
          revision,
          comment,
          user: rootState.user.user,
        });
      });
  },

  [mandantEditorActions.COMMIT_SECTION](
    { commit, dispatch },
    { clientElementId, subElementId, sectionId, revision, subRevision, clientId }
  ) {
    return services.mandant.aenderungsmodus
      .postTrackChangesSubelementSectionCommit(
        clientElementId,
        subElementId,
        sectionId,
        revision,
        subRevision
      )
      .then(() => {
        dispatch(mandantEditorActions.REMOVE_CLIENT_ELEMENT_ZWANGSUPDATE_KEY, {
          clientId,
          clientElementId,
          sectionId,
        });
        commit(mandantEditorMutations.REMOVE_SECTION_FROM_AENDERUNGSMODUS_HISTORY, sectionId);
        commit(mandantEditorMutations.SET_AENDERUNGSMODUS_SELECTED_HEAD, {});
        commit(mandantEditorMutations.SET_AENDERUNGSMODUS_SELECTED_BASE, {});
      });
  },

  [mandantEditorActions.DENY_SECTION](
    { commit, dispatch },
    { clientElementId, subElementId, sectionId, clientId }
  ) {
    return services.mandant.aenderungsmodus
      .postTrackChangesSubelementSectionDeny(clientElementId, subElementId, sectionId)
      .then(() => {
        dispatch(mandantEditorActions.REMOVE_CLIENT_ELEMENT_ZWANGSUPDATE_KEY, {
          clientId,
          clientElementId,
          sectionId,
        });
        commit(mandantEditorMutations.REMOVE_SECTION_FROM_AENDERUNGSMODUS_HISTORY, sectionId);
        commit(mandantEditorMutations.SET_AENDERUNGSMODUS_SELECTED_HEAD, {});
        commit(mandantEditorMutations.SET_AENDERUNGSMODUS_SELECTED_BASE, {});
      });
  },
};
